import { AxiosResponse } from "axios";
import type { CourseColorKey } from "@/lib/courseColors";
import httpDao from "../infrastructure/httpDao";

export interface DashboardAlert {
  id: string;
  type:
    | "admission"
    | "reschedule"
    | "payment"
    | "event"
    | "session"
    | "attendanceReport"
    | "trial"
    | "inquiry";
  priority: "high" | "medium" | "low";
  title: string;
  message: string;
  count: number;
  created_at: string;
}

export interface DashboardAlertsResponse {
  alerts: DashboardAlert[];
}

export type DashboardActionPriority = "high" | "medium" | "low";

export interface OperationsDashboardAction {
  id: string;
  title: string;
  description: string;
  count: number;
  priority: DashboardActionPriority;
  route: string;
}

export interface OperationsTodoResponse {
  generatedAt: string;
  facilityId: string | null;
  actionItems: OperationsDashboardAction[];
}

/** 要対応ToDoの集計元を更新したあと、ヘッダーへ即時再取得を知らせる。 */
export const OPERATIONS_TODOS_UPDATED_EVENT = "operations-todos-updated";

export const notifyOperationsTodosUpdated = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPERATIONS_TODOS_UPDATED_EVENT));
  }
};

/** 名簿表示用の名前（ふりがな付き）。kana は未登録なら null */
export interface RosterName {
  name: string;
  kana: string | null;
}

export interface OperationsDashboardResponse {
  generatedAt: string;
  facilityId: string | null;
  today: {
    date: string;
    sessionCount: number;
    plannedStudents: number;
    presentCount: number;
    absenceReportCount: number;
    lateReportCount: number;
    unconfirmedAttendanceReports: number;
    trialCount: number;
    recordedAttendanceCount: number;
  };
  operations: {
    pendingAdmissions: number;
    pendingReschedules: number;
    pendingTrialApplications: number;
    trialFollowUpDue: number;
    overduePaymentCount: number;
    overduePaymentTotal: number;
  };
  capacity: {
    activeSessionCount: number;
    totalCapacity: number;
    enrolledStudents: number;
    fillRate: number;
    availableSlots: number;
    nearFullSessions: Array<{
      id: string;
      courseName: string;
      facilityName: string;
      dayOfWeek: string;
      startTime: string;
      capacity: number;
      enrolled: number;
      fillRate: number;
    }>;
    openSessions: Array<{
      id: string;
      courseName: string;
      facilityName: string;
      dayOfWeek: string;
      startTime: string;
      capacity: number;
      enrolled: number;
      availableSlots: number;
    }>;
  };
  growth: {
    activeStudents: number;
    monthlyTrialApplications: number;
    monthlyTrialConversions: number;
    trialConversionRate: number;
    monthlyRevenue: number;
  };
  upcomingClasses: Array<{
    id: string;
    facilityId: string;
    courseId: string;
    courseName: string;
    /** 一覧上でコースを識別するためのカレンダー色 */
    courseColor?: CourseColorKey | null;
    facilityName: string;
    roomName: string | null;
    coachName: string | null;
    startTime: string;
    endTime: string;
    capacity: number;
    enrolled: number;
    availableSlots: number;
    fillRate: number;
    /** その回に予定されている体験申込の受講者名（キャンセル・入会済みを除く） */
    trials: RosterName[];
    /** 欠席（欠席報告 + 承認済み振替で不在になる受講者）の名前 */
    absentees: RosterName[];
    /** 欠席連絡・振替元と保存済み欠席を統合。旧APIでは未設定。 */
    registeredAbsentees?: RosterName[];
    /** 遅刻（遅刻報告）の名前 */
    lateComers: RosterName[];
    /** 承認済み振替でこの回に来る子の名前 */
    transfersIn: RosterName[];
    /** 出席予定 = 在籍 − 欠席 + 振替参加 + 体験参加 */
    expectedCount: number;
    /** 保存済み出欠のうち出席（通常 + 体験） */
    presentCount: number;
    /** 保存済み出欠件数（通常 + 体験） */
    recordedAttendanceCount: number;
    /** 選択日に適用された開講例外 */
    exceptionType:
      | "CANCELLED"
      | "COACH_CHANGE"
      | "TIME_CHANGE"
      | "ROOM_CHANGE"
      | null;
    exceptionReason: string | null;
    isCancelled: boolean;
    /** 休講の登録単位。手動休講は MANUAL */
    cancellationSource: "FACILITY" | "COURSE" | "SESSION" | "MANUAL" | null;
  }>;
  actionItems: OperationsDashboardAction[];
}

export interface DateRangeParams {
  from?: Date | string;
  to?: Date | string;
}

export interface EventAnalyticsParams extends DateRangeParams {
  eventId?: string;
  facilityId?: string;
}

export interface AnalyticsOverviewResponse {
  bookingCount: number;
  revenueTotal: number;
  newStudents: number;
  activeStaffCount: number;
  period: { from: string; to: string };
}

export interface CustomerAnalyticsResponse {
  period: { from: string; to: string };
  summary: {
    activeStudents: number;
    activeParents: number;
    newStudents: number;
    trialApplications: number;
    trialConversions: number;
    trialConversionRate: number;
  };
  funnel: Array<{ stage: string; count: number; rate: number }>;
  trend: Array<{
    month: string;
    trialApplications: number;
    admissions: number;
    activeStudents: number;
  }>;
}

export type CustomerGeoMetric =
  | "households"
  | "students"
  | "leads"
  | "conversion";

export interface CustomerGeoCell {
  h3Index: string;
  areaName: string | null;
  latitude: number;
  longitude: number;
  households: number;
  students: number;
  activeStudents: number;
  leads: number;
  trialHouseholds: number;
  convertedHouseholds: number;
  conversionRate: number | null;
}

export interface CustomerGeoFacility {
  id: string;
  name: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  locationStatus:
    | "PENDING"
    | "PROCESSING"
    | "READY"
    | "FAILED"
    | "MISSING_ADDRESS";
}

export interface CustomerGeoAnalysisResponse {
  generatedAt: string;
  resolution: number;
  metric: CustomerGeoMetric;
  permissions: { canViewHouseholds: boolean };
  geocoding: {
    configured: boolean;
    addressHouseholds: number;
    ready: number;
    pending: number;
    failed: number;
    coverageRate: number;
  };
  facilities: CustomerGeoFacility[];
  cells: CustomerGeoCell[];
  insights: Array<{
    kind: "density" | "follow_up" | "conversion";
    h3Index: string;
    title: string;
    description: string;
    value: string;
  }>;
}

export interface CustomerGeoHousehold {
  householdId: string;
  label: string;
  guardianName: string;
  address: string | null;
  latitude: number;
  longitude: number;
  status: "LEAD" | "INVITED" | "ACTIVE" | "DELETED";
  studentCount: number;
  students: Array<{
    id: string;
    name: string;
    isLead: boolean;
    facilityId: string | null;
    facilityName: string;
  }>;
}

export interface CustomerGeoHouseholdsResponse {
  households: CustomerGeoHousehold[];
  truncated: boolean;
}

export interface CustomerGeoAnalysisParams extends DateRangeParams {
  facilityId?: string;
  metric?: CustomerGeoMetric;
  resolution?: number;
}

export interface CustomerGeoHouseholdParams {
  facilityId?: string;
  north: number;
  south: number;
  east: number;
  west: number;
  limit?: number;
}

export interface EventAnalyticsResponse {
  period: { from: string; to: string };
  attribution: {
    method: "INFERRED_30_DAYS";
    windowDays: 30;
    description: string;
  };
  summary: {
    applications: number;
    confirmed: number;
    attended: number;
    attendanceEligibleConfirmed: number;
    attendanceRate: number | null;
    nonMemberApplications: number;
    nonMemberConfirmed: number;
    nonMemberAttendanceEligibleConfirmed: number;
    nonMemberAttended: number;
    membershipInvites: number;
    inferredMembershipConversions: number;
    membershipConversionEligibleCount: number;
    membershipConversionRate: number | null;
    maturingConversionCount: number;
  };
  trend: Array<{
    date: string;
    applications: number;
    confirmed: number;
    cancelled: number;
  }>;
  funnel: Array<{ stage: string; count: number; rate: number }>;
  events: Array<{
    eventId: string;
    eventName: string;
    eventDate: string;
    facilityName: string;
    status: string;
    capacity: number;
    applications: number;
    confirmed: number;
    attended: number;
    attendanceEligibleConfirmed: number;
    fillRate: number | null;
    attendanceRate: number | null;
    cancellationRate: number;
    nonMemberAttended: number;
    inferredMembershipConversions: number;
    membershipConversionEligibleCount: number;
    membershipConversionRate: number | null;
    maturingConversionCount: number;
  }>;
  /**
   * 前期間（指定期間の直前・同じ日数）との比較。
   * 前期間に応募が無い場合は null（画面側で比較表示を出さない）。
   */
  comparison: {
    previousPeriod: { from: string; to: string };
    summary: EventAnalyticsResponse["summary"];
  } | null;
}

export interface ManagementDashboardResponse {
  generatedAt: string;
  period: { from: string; to: string };
  assumptions: {
    payjpFeeRate: number;
    payjpFeeNote: string;
    serviceFeeNote: string;
  };
  revenue: {
    grossRevenue: number;
    refundAmount: number;
    netRevenue: number;
    tuitionRevenue: number;
    oneTimeRevenue: number;
    estimatedPayjpFees: number;
    serviceContractCost: number;
    estimatedContributionProfit: number;
    contributionMargin: number;
    overdueTotal: number;
    overduePaymentCount: number;
    /** 期間内の成功済み決済数（旧・売上分析から統合） */
    succeededPaymentCount: number;
    failedPaymentCount: number;
    paymentFailureRate: number;
  };
  pricing: {
    activePricingCount: number;
    recurringPricingCount: number;
    oneTimePricingCount: number;
    activeRecurringSubscriptions: number;
    recurringMrr: number;
    annualizedRecurringRevenue: number;
    averageRecurringPrice: number;
    revenuePerActiveStudent: number;
    topRecurringPlans: Array<{
      planId: string;
      name: string;
      amount: number;
      billingFrequency: "RECURRING" | "ONE_TIME";
      activeSubscriptions: number;
      monthlyRevenue: number;
    }>;
  };
  serviceContract: {
    status: string | null;
    planType: string | null;
    planName: string | null;
    monthlyFee: number;
    currentPeriodEnd: string | null;
    cardRegistered: boolean;
    maxStudents: number | null;
    maxFacilities: number | null;
    activeStudents: number;
    facilityCount: number;
    studentUsageRate: number;
    facilityUsageRate: number;
    needsAttention: boolean;
  };
  trend: Array<{
    month: string;
    grossRevenue: number;
    netRevenue: number;
    tuitionRevenue: number;
    oneTimeRevenue: number;
    refundAmount: number;
    estimatedPayjpFees: number;
    estimatedContributionProfit: number;
  }>;
  /** 会員の増減。売上 = 在籍 × 単価 の「在籍」側を分解する */
  membership: {
    activeStudents: number;
    newStudentsInPeriod: number;
    newSubscriptionsInPeriod: number;
    canceledSubscriptionsInPeriod: number;
    netSubscriptionChange: number;
    subscriptionTrend: Array<{ month: string; activeCount: number }>;
  };
  /** 稼働・空き枠（売上の上限を決める供給側KPI） */
  capacity: OperationsDashboardResponse["capacity"];
}

const normalizeDate = (value: Date | string): string => {
  if (value instanceof Date) {
    // 分析期間は時刻ではなく、管理画面で選択したローカル暦日を送る。
    // ISO時刻へ変換するとJSTの月初0時が前日になり、端日の集計が欠ける。
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return value;
};

const buildDateQuery = (range?: DateRangeParams): string => {
  const params = new URLSearchParams();
  if (range?.from) params.append("from", normalizeDate(range.from));
  if (range?.to) params.append("to", normalizeDate(range.to));
  const query = params.toString();
  return query ? `?${query}` : "";
};

export const statisticsRepository = {
  getDashboardAlerts: async (): Promise<
    AxiosResponse<DashboardAlertsResponse>
  > => {
    const res = await httpDao.get("/statistics/dashboard/alerts");
    return res;
  },

  getOperationsDashboard: async (
    facilityId?: string,
    date?: string
  ): Promise<AxiosResponse<OperationsDashboardResponse>> => {
    const params = new URLSearchParams();
    if (facilityId) params.append("facilityId", facilityId);
    if (date) params.append("date", date);
    const query = params.toString() ? `?${params.toString()}` : "";
    return httpDao.get(`/statistics/dashboard/operations${query}`);
  },

  getOperationsTodos: async (
    facilityId?: string
  ): Promise<AxiosResponse<OperationsTodoResponse>> => {
    const params = new URLSearchParams();
    if (facilityId) params.append("facilityId", facilityId);
    const query = params.toString() ? `?${params.toString()}` : "";
    return httpDao.get(`/statistics/dashboard/todos${query}`);
  },

  getManagementDashboard: async (
    range?: DateRangeParams,
    facilityId?: string
  ): Promise<AxiosResponse<ManagementDashboardResponse>> => {
    const params = new URLSearchParams();
    if (range?.from) params.append("from", normalizeDate(range.from));
    if (range?.to) params.append("to", normalizeDate(range.to));
    if (facilityId && facilityId !== "all") {
      params.append("facilityId", facilityId);
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    return httpDao.get(`/statistics/dashboard/management${query}`);
  },

  getAnalyticsOverview: async (
    range?: DateRangeParams
  ): Promise<AxiosResponse<AnalyticsOverviewResponse>> => {
    return httpDao.get(
      `/statistics/analytics/overview${buildDateQuery(range)}`
    );
  },

  getCustomerAnalytics: async (
    range?: DateRangeParams,
    facilityId?: string
  ): Promise<AxiosResponse<CustomerAnalyticsResponse>> => {
    const query = new URLSearchParams();
    if (range?.from) query.set("from", normalizeDate(range.from));
    if (range?.to) query.set("to", normalizeDate(range.to));
    if (facilityId && facilityId !== "all") {
      query.set("facilityId", facilityId);
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return httpDao.get(`/statistics/analytics/customers${suffix}`);
  },

  getCustomerGeoAnalysis: async (
    params: CustomerGeoAnalysisParams
  ): Promise<AxiosResponse<CustomerGeoAnalysisResponse>> => {
    return httpDao.get("/statistics/analytics/customers/geo", {
      params: {
        ...params,
        from: params.from ? normalizeDate(params.from) : undefined,
        to: params.to ? normalizeDate(params.to) : undefined,
      },
    });
  },

  getCustomerGeoHouseholds: async (
    params: CustomerGeoHouseholdParams
  ): Promise<AxiosResponse<CustomerGeoHouseholdsResponse>> => {
    return httpDao.get("/statistics/analytics/customers/geo/households", {
      params,
    });
  },

  getEventAnalytics: async (
    params?: EventAnalyticsParams
  ): Promise<AxiosResponse<EventAnalyticsResponse>> => {
    const query = new URLSearchParams();
    if (params?.from) query.set("from", normalizeDate(params.from));
    if (params?.to) query.set("to", normalizeDate(params.to));
    if (params?.eventId) query.set("eventId", params.eventId);
    if (params?.facilityId && params.facilityId !== "all") {
      query.set("facilityId", params.facilityId);
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return httpDao.get(`/statistics/analytics/events${suffix}`);
  },
};
